import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "@lab/src/lib/util";
import { Button } from "./button";

const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS_DE = [
	"Januar",
	"Februar",
	"März",
	"April",
	"Mai",
	"Juni",
	"Juli",
	"August",
	"September",
	"Oktober",
	"November",
	"Dezember",
];

function isSameDay(a: Date, b: Date) {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	);
}

function getDaysInMonth(year: number, month: number) {
	return new Date(year, month + 1, 0).getDate();
}

function getMonthGrid(year: number, month: number) {
	const firstDay = new Date(year, month, 1);
	// Monday = 0, Sunday = 6
	const startWeekday = (firstDay.getDay() + 6) % 7;
	const daysInMonth = getDaysInMonth(year, month);
	const daysInPrevMonth = getDaysInMonth(year, month - 1);

	const days: { date: Date; outside: boolean }[] = [];

	for (let i = startWeekday - 1; i >= 0; i--) {
		days.push({
			date: new Date(year, month - 1, daysInPrevMonth - i),
			outside: true,
		});
	}

	for (let d = 1; d <= daysInMonth; d++) {
		days.push({ date: new Date(year, month, d), outside: false });
	}

	const remaining = 7 - (days.length % 7);
	if (remaining < 7) {
		for (let d = 1; d <= remaining; d++) {
			days.push({ date: new Date(year, month + 1, d), outside: true });
		}
	}

	const weeks: { date: Date; outside: boolean }[][] = [];
	for (let i = 0; i < days.length; i += 7) {
		weeks.push(days.slice(i, i + 7));
	}
	return weeks;
}

interface CalendarProps {
	selected?: Date;
	onSelect?: (date: Date | undefined) => void;
	defaultMonth?: Date;
	className?: string;
}

function Calendar({
	selected,
	onSelect,
	defaultMonth,
	className,
}: CalendarProps) {
	const [viewDate, setViewDate] = React.useState(() => {
		const d = defaultMonth ?? selected ?? new Date();
		return { year: d.getFullYear(), month: d.getMonth() };
	});

	const today = new Date();
	const weeks = getMonthGrid(viewDate.year, viewDate.month);

	const handlePrev = () => {
		setViewDate((v) => {
			if (v.month === 0) return { year: v.year - 1, month: 11 };
			return { ...v, month: v.month - 1 };
		});
	};

	const handleNext = () => {
		setViewDate((v) => {
			if (v.month === 11) return { year: v.year + 1, month: 0 };
			return { ...v, month: v.month + 1 };
		});
	};

	return (
		<div className={cn("p-3", className)} data-slot="calendar">
			<div className="flex items-center justify-between mb-4">
				<Button
					variant="outline"
					className="h-7 w-7 p-0"
					onClick={handlePrev}
					aria-label="Vorheriger Monat"
				>
					<ChevronLeftIcon className="h-4 w-4" />
				</Button>
				<span className="text-sm font-medium select-none">
					{MONTHS_DE[viewDate.month]} {viewDate.year}
				</span>
				<Button
					variant="outline"
					className="h-7 w-7 p-0"
					onClick={handleNext}
					aria-label="Nächster Monat"
				>
					<ChevronRightIcon className="h-4 w-4" />
				</Button>
			</div>
			<table className="w-full border-collapse">
				<thead>
					<tr className="flex">
						{WEEKDAYS_DE.map((day) => (
							<th
								key={day}
								className="flex-1 text-muted-foreground text-[0.8rem] font-normal select-none text-center"
							>
								{day}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{weeks.map((week, wi) => (
						<tr key={wi} className="flex mt-1">
							{week.map(({ date, outside }, di) => {
								const isSelected =
									selected && isSameDay(date, selected);
								const isToday = isSameDay(date, today);

								return (
									<td
										key={di}
										className="flex-1 p-0 text-center"
									>
										<Button
											variant="ghost"
											className={cn(
												"h-8 w-8 p-0 font-normal",
												outside &&
													"text-muted-foreground opacity-50",
												isToday &&
													!isSelected &&
													"bg-accent text-accent-foreground",
												isSelected &&
													"bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
											)}
											onClick={() => onSelect?.(date)}
										>
											{date.getDate()}
										</Button>
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export { Calendar };
export type { CalendarProps };
